<script lang="ts" module>
  import { prefKey, readPref, writePref } from "$lib/prefs";

  export type CatalogMode = "edit" | "summary";

  /* «Редакция / Сводная» is remembered per view, per user and company. A view
     opens on «Сводная» until its reader asks to edit (owner, 2026-09-19:
     «когда нажали кнопку редакция — появляется возможность редактирования»). */
  export function loadCatalogMode(view: string, userId: number | undefined, slug: string): CatalogMode {
    return readPref<CatalogMode>(prefKey(`catalog.mode.${view}`, userId, slug)) === "edit" ? "edit" : "summary";
  }
  export function saveCatalogMode(view: string, userId: number | undefined, slug: string, mode: CatalogMode) {
    writePref(prefKey(`catalog.mode.${view}`, userId, slug), mode);
  }
</script>

<script lang="ts">
  /* The head of the «Склад» section (owner, 2026-09-19): the section title,
     and under it the switches between its four views — Склад, Продукты, Цены,
     Доступ. One database, one head: every view starts the same way, so the
     reader always knows which of the four they are looking at.

     A view whose table can be edited in place gets the «Редакция / Сводная»
     switch at the right end of the row.

     2026-09-22 (owner, photos): the grey «?» by the title and the «N партий /
     N товаров» line under it went — the «?» at the right of the four views
     says what the open one is for, and changes with it. A view with its own
     sub-buttons (Продукты: categories, supplier updates) passes them as
     `subnav`; its page buttons and the mode switch then sit, small, at the
     right of that row instead of beside the title. */
  import type { Snippet } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import { auth } from "$lib/auth";
  import { catalogViewPath, catalogViews, type CatalogView } from "$lib/catalogViews";

  let {
    slug,
    view,
    mode = $bindable(undefined),
    modeHint = "",
    onreselect,
    title,
    actions,
    subnav,
  }: {
    slug: string;
    view: CatalogView;
    /** Given, the «Редакция / Сводная» switch is drawn and remembered. */
    mode?: CatalogMode;
    /** What «Редакция» edits here, in a «?» beside the switch. */
    modeHint?: string;
    /** The open view's button clicked again — the page returns to its main table. */
    onreselect?: () => void;
    /** The help icons beside the title. */
    title?: Snippet;
    /** Page buttons on the right of the title (import, add…). */
    actions?: Snippet;
    /** The view's own sub-buttons, on a row of their own under the views. */
    subnav?: Snippet;
  } = $props();

  const views = $derived(catalogViews($auth, slug));
  const LABEL: Record<CatalogView, string> = {
    stock: "nav.stock",
    products: "nav.products",
    prices: "catalog.view.prices",
    access: "catalog.view.access",
  };
  const ICON: Record<CatalogView, string> = {
    stock: "warehouse",
    products: "inventory_2",
    prices: "calculate",
    access: "key",
  };
  /* What the open view is for, in the «?» right of the switch. */
  const HINT: Record<CatalogView, string> = {
    stock: "catalog.view.hint.stock",
    products: "catalog.view.hint.products",
    prices: "catalog.view.hint.prices",
    access: "catalog.view.hint.access",
  };

  function pick(v: CatalogView) {
    if (v === view) onreselect?.();
    else push(catalogViewPath(slug, v));
  }
  function setMode(m: CatalogMode) {
    mode = m;
    saveCatalogMode(view, $auth.user?.id, slug, m);
  }
</script>

<div class="ch">
  <div class="ch-top">
    <div class="title-row">
      <h1 class="ch-title">{$_("nav.stock")}</h1>
      {@render title?.()}
    </div>
    {#if actions && !subnav}<div class="ch-actions">{@render actions()}</div>{/if}
  </div>

  <div class="ch-views">
    <div class="ch-views-left">
      <div class="ch-switch" role="tablist" aria-label={$_("nav.stock")}>
        {#each views as v (v)}
          <button
            type="button"
            role="tab"
            class="ch-view"
            class:on={v === view}
            aria-selected={v === view}
            onclick={() => pick(v)}
          >
            <span class="material-icons-outlined" aria-hidden="true">{ICON[v]}</span>
            {$_(LABEL[v])}
          </button>
        {/each}
      </div>
      {#key view}<InfoTooltip text={$_(HINT[view])} />{/key}
    </div>
    {#if mode !== undefined && !subnav}{@render modeSwitch()}{/if}
  </div>

  {#if subnav}
    <div class="ch-sub">
      <div class="ch-sub-left">{@render subnav()}</div>
      <div class="ch-sub-right">
        {#if actions}{@render actions()}{/if}
        {#if mode !== undefined}{@render modeSwitch()}{/if}
      </div>
    </div>
  {/if}
</div>

{#snippet modeSwitch()}
  <div class="ch-mode-wrap" class:small={!!subnav}>
    <div class="ch-mode" role="group" aria-label={$_("prices.mode.edit")}>
      <button type="button" class="ch-mode-btn" class:on={mode === "edit"} onclick={() => setMode("edit")}>
        <span class="material-icons-outlined" aria-hidden="true">edit</span>
        {$_("prices.mode.edit")}
      </button>
      <button type="button" class="ch-mode-btn" class:on={mode === "summary"} onclick={() => setMode("summary")}>
        <span class="material-icons-outlined" aria-hidden="true">table_view</span>
        {$_("prices.mode.summary")}
      </button>
    </div>
    {#if modeHint}<InfoTooltip text={modeHint} />{/if}
  </div>
{/snippet}

<style>
  .ch { display: flex; flex-direction: column; gap: 10px; }
  .ch-top { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 12px 20px; }
  .title-row { display: flex; align-items: center; gap: 8px; }
  .ch-title { margin: 0; font-size: 22px; font-weight: 700; color: var(--color-surface-darker); }
  .ch-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }

  .ch-views { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; }
  .ch-views-left { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .ch-switch {
    display: inline-flex; flex-wrap: wrap; border: 1px solid var(--color-surface-dark);
    border-radius: 8px; overflow: hidden; background: #fff;
  }
  .ch-view {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; background: #fff;
    font-family: inherit; font-size: 14px; font-weight: 600; color: var(--color-surface-dark); cursor: pointer;
    white-space: nowrap;
  }
  .ch-view + .ch-view { border-left: 1px solid var(--color-divider); }
  .ch-view .material-icons-outlined { font-size: 18px; }
  .ch-view:hover { color: var(--color-primary); }
  .ch-view.on { background: var(--color-surface-dark); color: #fff; }

  .ch-mode-wrap { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; }
  .ch-mode {
    display: inline-flex; border: 1px solid var(--color-divider); border-radius: 6px; overflow: hidden; background: #fff;
  }
  .ch-mode-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border: none; background: #fff;
    font-family: inherit; font-size: 13px; color: var(--color-text-muted); cursor: pointer; white-space: nowrap;
  }
  .ch-mode-btn + .ch-mode-btn { border-left: 1px solid var(--color-divider); }
  .ch-mode-btn .material-icons-outlined { font-size: 16px; }
  .ch-mode-btn:hover { color: var(--color-primary); }
  .ch-mode-btn.on { background: var(--color-primary); color: #fff; font-weight: 600; }
  .ch-mode-wrap.small .ch-mode-btn { padding: 4px 9px; font-size: 12px; }
  .ch-mode-wrap.small .ch-mode-btn .material-icons-outlined { font-size: 14px; }

  /* A view's sub-buttons on the left; its page buttons and the mode switch,
     small, on the right. */
  .ch-sub { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; }
  .ch-sub-left { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
  .ch-sub-right { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-left: auto; }
  .ch-sub-right :global(.btn) { padding: 5px 11px; font-size: 12px; gap: 5px; }
  .ch-sub-right :global(.btn .material-icons-outlined) { font-size: 16px; }

  /* Phone: the four views share the width evenly, the mode switch below. */
  @media (max-width: 700px) {
    .ch-switch { display: grid; grid-template-columns: repeat(2, 1fr); width: 100%; }
    .ch-view { justify-content: center; padding: 9px 8px; font-size: 13px; }
    .ch-view + .ch-view { border-left: none; }
    .ch-view:nth-child(even) { border-left: 1px solid var(--color-divider); }
    .ch-view:nth-child(n + 3) { border-top: 1px solid var(--color-divider); }
    .ch-mode-wrap { margin-left: 0; }
    .ch-views-left { width: 100%; flex-wrap: nowrap; }
    .ch-sub-right { margin-left: 0; width: 100%; }
  }
</style>
